import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { BLOG_POSTS, getLocalizedPosts } from "@/lib/mock/blog";
import { loadBlogBodies } from "@/lib/mock/blog_bodies";
import { AUTHORS, articleCounts } from "@/lib/mock/authors";
import { useI18n } from "@/context/I18nContext";
import { buildSubmissionContext, SUBMISSION_SOURCES } from "@/lib/submission-context";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  BookOpen,
  Clock,
  ChevronRight,
  Mail,
  ShieldCheck,
  Award,
  ArrowUpRight,
  Flame,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

import { getSeoMeta, localizedCompanyName } from "@/lib/utils/seo";
import { FaqSection } from "@/components/layout/FaqSection";
import { faqHeadScript } from "@/lib/utils/faq-schema";

export const Route = createFileRoute("/blog/")({
  head: () => {
    const meta = getSeoMeta("blog");
    return {
      meta: [
        { title: meta.title },
        { name: "description", content: meta.description },
        { property: "og:title", content: meta.title },
        { property: "og:description", content: meta.description },
        { name: "twitter:title", content: meta.title },
        { name: "twitter:description", content: meta.description },
      ],
      // Feed autodiscovery. Without it the feed exists but nothing points at
      // it, so a reader handed /blog has no way to find it.
      links: [
        {
          rel: "alternate",
          type: "application/rss+xml",
          title: "Aqua MAG — VARS Aquaculture",
          href: "/blog.rss.xml",
        },
      ],
      scripts: [faqHeadScript("blog", "/blog")],
    };
  },
  component: BlogIndex,
});

type ReactFormEvent = React.FormEvent;

function BlogIndex() {
  const { lang, t } = useI18n();
  const counts = articleCounts();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Article bodies are a per-locale dynamic import (blog_bodies), fetched the
  // first time someone actually searches rather than on every visit to /blog —
  // the listing itself only needs titles and excerpts. Until it resolves, and
  // always for English, the search reads the English body already in blog.ts.
  const [bodies, setBodies] = useState<Record<string, string>>({});
  const searching = searchQuery.trim() !== "";
  useEffect(() => {
    if (!searching) return;
    let cancelled = false;
    void loadBlogBodies(lang).then((loaded) => {
      if (!cancelled) setBodies(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [searching, lang]);

  const localizedPosts = useMemo(
    () =>
      getLocalizedPosts(lang)
        .map((post) => (bodies[post.slug] ? { ...post, body: bodies[post.slug] } : post))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [lang, bodies],
  );

  const CATEGORIES = [
    { id: "all", label: t("blog.categories.all") },
    {
      id: "aquaculture-10",
      label: t("blog.categories.aquaculture-10"),
    },
    {
      id: "hatchery-feed-design-6",
      label: t("blog.categories.hatchery-feed-design-6"),
    },
    {
      id: "feed-innovation-sustainability-8",
      label: t("blog.categories.feed-innovation-sustainability-8"),
    },
    {
      id: "vars-company-1",
      label: t("blog.categories.vars-company-1"),
    },
    { id: "projects-4", label: t("blog.categories.projects-4") },
  ];

  const filteredPosts = localizedPosts.filter((p) => {
    const matchesCategory = selectedCategory === "all" || p.categorySlug === selectedCategory;
    const matchesSearch =
      searchQuery.trim() === "" ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.body.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = localizedPosts[0]; // First main cover story
  const editorsPicks = localizedPosts.slice(1, 4); // 3 picks for sidebar

  const showFeaturedHero = selectedCategory === "all" && searchQuery.trim() === "";
  const gridPosts = showFeaturedHero
    ? filteredPosts.filter((p) => p.slug !== featuredPost.slug)
    : filteredPosts;

  return (
    <div className="bg-background min-h-screen pb-20">
      {/* Editorial Journal Masthead */}
      <section className="border-b border-border/80 bg-navy text-white relative overflow-hidden py-14">
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/80" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-white/10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-mint/15 text-mint text-xs font-bold uppercase tracking-widest mb-3">
                <BookOpen className="h-3.5 w-3.5" /> {t("blog.index.eyebrow")}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-none">
                AQUA <span className="text-mint font-light">MAG</span>
              </h1>
              <p className="mt-2 text-sm text-white/80 max-w-xl font-medium">
                {t("blog.index.subtitle")}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-white/60 font-semibold uppercase tracking-wider">
              {/* Was "ISSN 2984-1029 • Quarterly Review • Vol. VIII · Issue 24".
                  No ISSN has been registered, so that number identified nothing;
                  the volume and issue counters were invented alongside it, and
                  the journal does not publish quarterly — the posts run roughly
                  monthly. A technical journal is a real thing to be; a
                  registered periodical is not something to claim until it is. */}
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-mint" /> {t("blog.index.technicalBadge")}
              </span>
              <span>•</span>
              <span>Published by {localizedCompanyName(lang)}</span>
            </div>
          </div>

          {/* Editorial Category Navigation Bar */}
          <div className="flex items-center gap-2 overflow-x-auto pt-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.id}
                to={cat.id === "all" ? "/blog" : "/blog/$category"}
                params={cat.id === "all" ? undefined : { category: cat.id }}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                  selectedCategory === cat.id
                    ? "bg-mint text-navy shadow-md scale-105"
                    : "bg-white/10 text-white/80 hover:bg-white/20 hover:text-white"
                }`}
              >
                {cat.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Main Magazine Layout Container */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-10 space-y-16">
        {/* Top Hero Editorial Section (8:4 Layout) */}
        {showFeaturedHero && (
          <section className="grid lg:grid-cols-12 gap-8 items-stretch">
            {/* Featured Article Cover Story (8 Cols) */}
            <div className="lg:col-span-8 flex flex-col justify-between glass-card rounded-3xl overflow-hidden border border-border/80 bg-background shadow-xl hover:shadow-2xl transition-all duration-300">
              <Link
                to="/blog/$category/$slug"
                params={{ category: featuredPost.categorySlug, slug: featuredPost.slug }}
                className="group relative flex-1 flex flex-col"
              >
                <div className="aspect-[16/9] w-full overflow-hidden bg-muted relative">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/30 to-transparent" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold shadow-md">
                    <Flame className="h-3.5 w-3.5 fill-current" /> {t("blog.index.featuredCover")}
                  </div>
                  {/* Frosted white panel: covers vary from dark tanks to bright
                      green algae macros, so the title gets its own readable
                      ground instead of relying on the photo's contrast. */}
                  <div className="absolute bottom-4 left-4 right-4 space-y-2 rounded-2xl bg-white/70 p-4 backdrop-blur-md shadow-lg">
                    <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-primary">
                      <span>{featuredPost.category}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />{" "}
                        {t("blog.index.readMinutes").replace(
                          "{minutes}",
                          String(featuredPost.readMinutes),
                        )}
                      </span>
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight text-navy group-hover:text-primary transition-colors">
                      {featuredPost.title}
                    </h2>
                  </div>
                </div>

                <div className="p-6 space-y-4 bg-background">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <span className="float-left text-3xl font-display font-bold text-navy leading-none mr-2">
                      E
                    </span>
                    {featuredPost.excerpt}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-border/60">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9 border border-border">
                        <AvatarFallback className="bg-navy text-xs font-bold text-white">
                          {featuredPost.authorInitials}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="text-xs font-bold text-navy">{featuredPost.author}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {t("blog.byline.technicalEditor")}
                        </div>
                      </div>
                    </div>

                    <span className="inline-flex items-center text-xs font-bold text-primary group-hover:translate-x-1 transition-transform">
                      {t("blog.index.readArticle")} <ArrowUpRight className="ml-1 h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Editor's Picks & Technical Reports Sidebar (4 Cols) */}
            <div className="lg:col-span-4 glass-card rounded-3xl p-6 border border-border/80 bg-surface-alt/50 shadow-md flex flex-col justify-between space-y-6">
              <div>
                <div className="flex items-center gap-2 pb-4 border-b border-border/60">
                  <Award className="h-5 w-5 text-mint-ink" />
                  <h3 className="font-display text-lg font-bold text-navy">
                    {t("blog.index.editorsPicks")}
                  </h3>
                </div>

                <div className="divide-y divide-border/60">
                  {editorsPicks.map((pick) => (
                    <Link
                      key={pick.slug}
                      to="/blog/$category/$slug"
                      params={{ category: pick.categorySlug, slug: pick.slug }}
                      className="group block py-4 space-y-1.5 transition-colors"
                    >
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground font-semibold">
                        <span className="text-primary uppercase tracking-wider">
                          {pick.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {pick.readMinutes}m
                        </span>
                      </div>
                      <h4 className="font-display text-sm font-bold text-navy group-hover:text-primary transition-colors leading-snug">
                        {pick.title}
                      </h4>
                      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                        {pick.excerpt}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-navy text-white space-y-2">
                <div className="text-xs font-bold text-mint flex items-center gap-1.5">
                  <Award className="h-4 w-4" /> {t("blog.index.standardsTitle")}
                </div>
                <p className="text-[11px] text-white/80 leading-relaxed">
                  {t("blog.index.standardsDesc")}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Magazine Article Grid Section */}
        <section className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-border/60">
            <div>
              <h2 className="font-display text-2xl font-bold text-navy">
                {selectedCategory === "all"
                  ? t("blog.index.latestIssues")
                  : CATEGORIES.find((c) => c.id === selectedCategory)?.label}
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {t("blog.index.showingArticles").replace("{count}", String(gridPosts.length))}
              </p>
            </div>

            {/* Real-time search bar */}
            <div className="relative w-full sm:w-72">
              <Input
                type="text"
                placeholder={t("blog.index.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-surface-alt border-border rounded-xl text-xs h-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy text-xs font-bold"
                >
                  {t("blog.index.clear")}
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {gridPosts.map((post) => (
              <Link
                key={post.slug}
                to="/blog/$category/$slug"
                params={{ category: post.categorySlug, slug: post.slug }}
                className="glass-card rounded-2xl overflow-hidden border border-border/80 bg-background shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col justify-between group"
              >
                <div>
                  <div className="aspect-[16/10] overflow-hidden bg-muted relative">
                    {post.image && (
                      <img
                        src={post.image}
                        alt={post.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-lg bg-navy/80 text-white text-[10px] font-bold uppercase tracking-wider backdrop-blur-md">
                      {post.category}
                    </div>
                  </div>

                  <div className="p-5 space-y-3">
                    <h3 className="font-display text-base font-bold text-navy leading-snug group-hover:text-primary transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                  </div>
                </div>

                <div className="p-5 pt-0 flex items-center justify-between text-xs text-muted-foreground border-t border-border/40 mt-4">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="bg-navy text-[10px] font-bold text-white">
                        {post.authorInitials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-navy">{post.author}</span>
                  </div>

                  <span className="flex items-center gap-1 font-semibold text-primary">
                    <Clock className="h-3 w-3" />{" "}
                    {t("blog.index.readMinutes").replace("{minutes}", String(post.readMinutes))}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Expert Editorial Board & Authors */}
        <section className="space-y-8 my-16">
          <div className="border-b border-border pb-4">
            <h2 className="font-display text-2xl font-bold text-navy">{t("blog.board.title")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("blog.board.lead")}</p>
          </div>

          {/*
            Rendered from AUTHORS rather than written out inline. Counts are
            derived dynamically from BLOG_POSTS.
          */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {AUTHORS.map((author) => (
              <div
                key={author.initials}
                className="glass-card rounded-3xl p-6 border border-border bg-surface-alt/20 space-y-4 flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-navy text-sm font-bold text-white">
                        {author.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      {/* Linked where the person has a public profile. An
                          editorial-authority section is only worth anything if
                          the people in it can be checked. */}
                      <h3 className="font-display text-sm font-bold text-navy">
                        {author.profileUrl ? (
                          <a
                            href={author.profileUrl}
                            target="_blank"
                            rel="noopener noreferrer author"
                            className="hover:text-primary transition-colors"
                          >
                            {author.name}
                          </a>
                        ) : (
                          author.name
                        )}
                      </h3>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{author.bio}</p>
                </div>
                <div
                  className={`pt-4 border-t border-border/60 text-xs font-semibold flex items-center justify-between ${
                    author.advisory ? "text-muted-foreground" : "text-primary"
                  }`}
                >
                  <span>
                    {author.advisory
                      ? t("blog.board.advisory")
                      : t("blog.board.articleCount").replace(
                          "{count}",
                          String(counts[author.initials] ?? 0),
                        )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* The Aqua MAG newsletter block was removed: there is no newsletter to
            subscribe to. It also carried two claims nothing backed — "over
            3,000 hatchery engineers, marine biologists and aquaculture
            investors" as a subscriber count, and "peer-reviewed articles".
            /api/newsletter is left in place so the UI can come back when there
            is a real digest to send. */}
      </div>

      <FaqSection page="blog" />
    </div>
  );
}
